import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './index';
import { COLLECTIONS } from './schema';

const jobsRef       = collection(db, COLLECTIONS.jobs);
const timesheetsRef = collection(db, COLLECTIONS.timesheets);
const photosRef     = collection(db, COLLECTIONS.photos);
const usersRef      = collection(db, COLLECTIONS.users);

export const getJobs = async (assignedTo = null) => {
  try {
    if (assignedTo) {
      // For field workers, query WITHOUT orderBy to avoid composite index issues
      // Firestore requires a composite index for (assignedTo + createdAt desc)
      // We do client-side sorting instead
      const q1 = query(jobsRef, where('assignedTo', '==', assignedTo));
      const q2 = query(jobsRef, where('assignedTo', 'array-contains', assignedTo));
      
      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      const jobs1 = snap1.docs.map((d) => ({ id: d.id, ...d.data() }));
      const jobs2 = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Merge and deduplicate by id
      const jobMap = new Map();
      [...jobs1, ...jobs2].forEach(j => jobMap.set(j.id, j));
      const allJobs = Array.from(jobMap.values());
      
      // Client-side sort: completed at bottom, newest first
      return allJobs.sort((a, b) => {
        const aCompleted = a.status === 'completed' ? 1 : 0;
        const bCompleted = b.status === 'completed' ? 1 : 0;
        if (aCompleted !== bCompleted) return aCompleted - bCompleted;
        const aDate = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const bDate = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return bDate - aDate;
      });
    }
    // Admin query: no where clause needed, orderBy works with single-field index
    const q = query(jobsRef, orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('getJobs error:', e);
    return [];
  }
};

export const getJobById = async (id) => {
  try {
    const snap = await getDoc(doc(db, 'jobs', id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (e) {
    console.error('getJobById error:', e);
    return null;
  }
};

export const createJob = async (data) => {
  try {
    const ref = await addDoc(jobsRef, {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: ref.id, error: null };
  } catch (e) {
    return { id: null, error: e.message };
  }
};

export const updateJob = async (id, data) => {
  try {
    await updateDoc(doc(db, 'jobs', id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
    return { error: null };
  } catch (e) {
    return { error: e.message };
  }
};

export const deleteJob = async (id) => {
  try {
    await deleteDoc(doc(db, 'jobs', id));
    return { error: null };
  } catch (e) {
    return { error: e.message };
  }
};

export const getTimesheets = async (jobId = null, workerId = null, fetchAll = false) => {
  try {
    let q;
    if (fetchAll) {
      // Admin: fetch ALL timesheets
      q = query(timesheetsRef, orderBy('createdAt', 'desc'));
    } else if (jobId) {
      q = query(timesheetsRef, where('jobId', '==', jobId), orderBy('createdAt', 'desc'));
    } else if (workerId) {
      q = query(timesheetsRef, where('workerId', '==', workerId), orderBy('createdAt', 'desc'));
    } else {
      q = query(timesheetsRef, orderBy('createdAt', 'desc'));
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('getTimesheets error:', e);
    return [];
  }
};

export const createTimesheet = async (data) => {
  try {
    const ref = await addDoc(timesheetsRef, {
      ...data,
      createdAt: serverTimestamp(),
    });
    return { id: ref.id, error: null };
  } catch (e) {
    return { id: null, error: e.message };
  }
};

export const updateTimesheet = async (id, data) => {
  try {
    await updateDoc(doc(db, 'timesheets', id), data);
    return { error: null };
  } catch (e) {
    return { error: e.message };
  }
};

export const deleteTimesheet = async (id) => {
  try {
    await deleteDoc(doc(db, 'timesheets', id));
    return { error: null };
  } catch (e) {
    return { error: e.message };
  }
};

export const getPhotos = async (jobId) => {
  try {
    const q = query(photosRef, where('jobId', '==', jobId), orderBy('uploadedAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('getPhotos error:', e);
    return [];
  }
};

export const savePhotoMetadata = async (data) => {
  try {
    const ref = await addDoc(photosRef, {
      ...data,
      uploadedAt: serverTimestamp(),
    });
    return { id: ref.id, error: null };
  } catch (e) {
    return { id: null, error: e.message };
  }
};

export const deletePhotoMetadata = async (id) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.photos, id));
    return { error: null };
  } catch (e) {
    return { error: e.message };
  }
};

// Update job photo count (legacy + new photos collection)
export const updateJobPhotoCount = async (jobId) => {
  try {
    // Count photos in the photos collection
    const q = query(photosRef, where('jobId', '==', jobId));
    const snap = await getDocs(q);
    const collectionCount = snap.docs.length;

    // Get legacy jobPhotos count
    const jobSnap = await getDoc(doc(db, COLLECTIONS.jobs, jobId));
    const legacyCount = jobSnap.exists() ? (jobSnap.data().jobPhotos?.length || 0) : 0;

    const totalCount = collectionCount + legacyCount;
    await updateDoc(doc(db, COLLECTIONS.jobs, jobId), { photoCount: totalCount });
    return { error: null, count: totalCount };
  } catch (e) {
    console.error('updateJobPhotoCount error:', e);
    return { error: e.message };
  }
};

export const getUserProfile = async (uid) => {
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.users, uid));
    return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
  } catch (e) {
    console.error('getUserProfile error:', e);
    return null;
  }
};

export const updateUserProfile = async (uid, data) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.users, uid), data);
    return { error: null };
  } catch (e) {
    return { error: e.message };
  }
};

export const getAllUsers = async () => {
  try {
    const q = query(usersRef, orderBy('displayName', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
  } catch (e) {
    console.error('getAllUsers error:', e);
    return [];
  }
};

export const getWorkers = async () => {
  try {
    const q = query(usersRef, where('role', '==', 'fieldWorker'));
    const snap = await getDocs(q);
    const workers = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
    console.log('getWorkers found:', workers.length, workers.map(w => ({ uid: w.uid, name: w.displayName, role: w.role })));
    
    if (workers.length === 0) {
      // Diagnostic: scan ALL users to see what roles actually exist
      console.warn('[DIAGNOSTIC] No fieldWorker found — scanning ALL users...');
      const allSnap = await getDocs(usersRef);
      const allUsers = allSnap.docs.map(d => ({ uid: d.id, ...d.data() }));
      console.warn('[DIAGNOSTIC] All users in DB:', allUsers.length, allUsers.map(u => ({ uid: u.uid, name: u.displayName, role: u.role })));
      
      // Also check common role variants
      const variants = ['worker', 'Worker', 'field worker', 'FieldWorker', 'fieldworker'];
      for (const variant of variants) {
        const qVar = query(usersRef, where('role', '==', variant));
        const snapVar = await getDocs(qVar);
        if (snapVar.docs.length > 0) {
          console.warn(`[DIAGNOSTIC] Found users with role='${variant}':`, snapVar.docs.length);
        }
      }
    }
    return workers;
  } catch (e) {
    console.error('getWorkers error:', e.message, e.code);
    return [];
  }
};

export const deleteUserDoc = async (uid) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.users, uid));
    return { error: null };
  } catch (e) {
    console.error('deleteUserDoc error:', e);
    return { error: e.message };
  }
};

// ─── Signed Timesheets ───
const signedTimesheetsRef = collection(db, COLLECTIONS.signedTimesheets);

export const getSignedTimesheets = async (jobId = null) => {
  try {
    let q = query(signedTimesheetsRef, orderBy('createdAt', 'desc'));
    if (jobId) {
      q = query(signedTimesheetsRef, where('jobId', '==', jobId), orderBy('createdAt', 'desc'));
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('getSignedTimesheets error:', e);
    return [];
  }
};

export const createSignedTimesheet = async (data) => {
  try {
    const ref = await addDoc(signedTimesheetsRef, {
      ...data,
      createdAt: serverTimestamp(),
    });
    return { id: ref.id, error: null };
  } catch (e) {
    return { id: null, error: e.message };
  }
};

export const deleteSignedTimesheet = async (id) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.signedTimesheets, id));
    return { error: null };
  } catch (e) {
    return { error: e.message };
  }
};

export const updateSignedTimesheet = async (id, data) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.signedTimesheets, id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
    return { error: null };
  } catch (e) {
    return { error: e.message };
  }
};

export const getSignedTimesheetById = async (id) => {
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.signedTimesheets, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (e) {
    console.error('getSignedTimesheetById error:', e);
    return null;
  }
};

export const getAuthEvents = async () => {
  try {
    const q = query(collection(db, COLLECTIONS.authEvents), orderBy('timestamp', 'desc'));
    const snap = await getDocs(q);
    const events = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    
    // Fetch user emails for events that only have userId
    const userIds = events.filter(e => e.userId && !e.userEmail).map(e => e.userId);
    const uniqueUserIds = [...new Set(userIds)];
    
    if (uniqueUserIds.length > 0) {
      const userPromises = uniqueUserIds.map(async (uid) => {
        try {
          const userSnap = await getDoc(doc(db, COLLECTIONS.users, uid));
          return { uid, email: userSnap.exists() ? userSnap.data().email : null };
        } catch (err) {
          return { uid, email: null };
        }
      });
      const userMap = await Promise.all(userPromises);
      const emailMap = userMap.reduce((acc, { uid, email }) => {
        acc[uid] = email;
        return acc;
      }, {});
      
      // Add email to each event
      events.forEach(event => {
        if (event.userId && !event.userEmail && emailMap[event.userId]) {
          event.userEmail = emailMap[event.userId];
        }
      });
    }
    
    return { data: events, error: null };
  } catch (e) {
    console.error('getAuthEvents error:', e);
    return { data: [], error: e.message };
  }
};
